/**
 * DocumentUploader — three paths:
 *   1) Upload local file → the EDM storage adapter via
 *      /api/edm/projects/{pid}/attachments  (an attachment, NOT a register entry:
 *      it carries no revision and no status code, and nothing derives "current"
 *      from it)
 *   2) Pick document(s) from the EDM register (searchable list)
 *   3) Manually paste any URL (fallback)
 * Emits onUploaded(fileUrl, filename) — for a multi-select DMS pick,
 * fires once per selected document.
 */
import { useState, useRef, useEffect } from 'react';
import { Upload, FolderOpen, Loader2, FileText, CheckCircle2, Search, X, Link2 } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';

const normalise = (data) => (data?.documents || []).map((d) => ({
  doc_id: d.document_id,
  doc_number: d.document_no,
  title: d.title,
  discipline: d.discipline,
  status: d.doc_type,
  folder: d.area,
  revision: d.latestRevision?.revision_code,
}));

export default function DocumentUploader({
  projectId,
  currentUrl,
  label = 'Attach Document',
  onUploaded,
  multi = false,
  testId = 'doc-uploader',
}) {
  const [busy, setBusy] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [urlInputOpen, setUrlInputOpen] = useState(false);
  const [urlInput, setUrlInput] = useState('');
  // DMS Picker state
  const [docs, setDocs] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const fileRef = useRef(null);

  const upload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      const { data } = await api.post(`/edm/projects/${projectId}/attachments`, fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`Uploaded · ${f.name}`);
      onUploaded?.(data.file_url, data.filename);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed');
    }
    setBusy(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const openPicker = async () => {
    setPickerOpen(true);
    setLoading(true);
    setSelected(new Set());
    try {
      const { data } = await api.get(`/edm/projects/${projectId}/documents`);
      setDocs(normalise(data));
    } catch (err) {
      toast.error('Could not load the document register');
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!pickerOpen) return;
    const t = setTimeout(async () => {
      if (!q.trim()) return;
      setLoading(true);
      try {
        const { data } = await api.get(`/edm/projects/${projectId}/documents`, { params: { q } });
        setDocs(normalise(data));
      } catch {}
      setLoading(false);
    }, 250);
    return () => clearTimeout(t);
  }, [q, pickerOpen, projectId]);

  const toggle = (id) => {
    const next = new Set(selected);
    if (multi) {
      next.has(id) ? next.delete(id) : next.add(id);
    } else {
      next.clear();
      next.add(id);
    }
    setSelected(next);
  };

  const confirmPick = () => {
    const chosen = docs.filter(d => selected.has(d.doc_id || d.id));
    if (chosen.length === 0) return toast.info('Select a document first');
    chosen.forEach(d => {
      const url = `/engineering/documents/${d.doc_id}`;
      const name = d.doc_number ? `${d.doc_number} · ${d.title || ''}`.trim() : (d.title || 'DMS document');
      onUploaded?.(url, name);
    });
    toast.success(`Linked ${chosen.length} document${chosen.length === 1 ? '' : 's'}`);
    setPickerOpen(false);
  };

  const saveLink = () => {
    if (!urlInput.trim()) return toast.info('Paste a link first');
    onUploaded?.(urlInput.trim(), urlInput.split('/').pop() || 'linked-doc');
    toast.success('Link saved');
    setUrlInputOpen(false);
    setUrlInput('');
  };

  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      {currentUrl && (
        <a href={currentUrl} target="_blank" rel="noreferrer"
          className="inline-flex items-center gap-1 text-[10px] underline"
          style={{ color: 'var(--accent-primary)' }}
          data-testid={`${testId}-current`}>
          <CheckCircle2 className="w-3 h-3" /> Attached
        </a>
      )}
      <input ref={fileRef} type="file" onChange={upload} className="hidden" data-testid={`${testId}-file`} />
      <Button size="sm" variant="outline" onClick={() => fileRef.current?.click()}
        disabled={busy} className="h-6 text-[10px]" data-testid={`${testId}-upload-btn`}>
        {busy ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Upload className="w-3 h-3 mr-1" />}
        {label}
      </Button>
      <Button size="sm" variant="ghost" onClick={openPicker}
        className="h-6 text-[10px]" data-testid={`${testId}-picker-btn`}
        title="Pick from the Panorix document register">
        <FolderOpen className="w-3 h-3 mr-1" /> Attach document
      </Button>
      {urlInputOpen ? (
        <div className="flex items-center gap-1">
          <input value={urlInput} onChange={(e) => setUrlInput(e.target.value)}
            placeholder="paste URL…"
            className="px-2 py-1 rounded-md text-[10px] border w-44"
            style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
            data-testid={`${testId}-url-input`} />
          <Button size="sm" onClick={saveLink} className="h-6 text-[10px]" data-testid={`${testId}-save-link`}>Save</Button>
          <button onClick={() => setUrlInputOpen(false)} className="text-[10px]" title="Cancel"><X className="w-3 h-3" /></button>
        </div>
      ) : (
        <button onClick={() => setUrlInputOpen(true)} className="text-[10px]" title="Paste URL"
          style={{ color: 'var(--text-muted)' }} data-testid={`${testId}-paste-url-btn`}>
          <Link2 className="w-3 h-3" />
        </button>
      )}

      {/* DMS Picker Modal */}
      {pickerOpen && (
        <div className="fixed inset-0 z-[100] bg-black/50 flex items-center justify-center p-4"
          onClick={() => setPickerOpen(false)} data-testid={`${testId}-picker-modal`}>
          <div onClick={(e) => e.stopPropagation()}
            className="w-full max-w-2xl max-h-[70vh] flex flex-col rounded-lg shadow-2xl"
            style={{ background: 'var(--card-bg)', border: '1px solid var(--border-color)' }}>
            <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
              <FolderOpen className="w-4 h-4" style={{ color: 'var(--accent-primary)' }} />
              <div className="font-semibold text-sm" style={{ color: 'var(--text-primary)' }}>
                Panorix document register — {multi ? 'Select one or more documents' : 'Select a document'}
              </div>
              <button onClick={() => setPickerOpen(false)} className="ml-auto"><X className="w-4 h-4" style={{ color: 'var(--text-muted)' }} /></button>
            </div>
            <div className="p-3 border-b flex items-center gap-2" style={{ borderColor: 'var(--border-color)' }}>
              <div className="relative flex-1">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                <input value={q} onChange={(e) => setQ(e.target.value)}
                  placeholder="Search by document number, title, tags…"
                  className="w-full pl-7 pr-2 py-1.5 rounded-md text-xs border"
                  style={{ background: 'var(--input-bg)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                  data-testid={`${testId}-picker-search`} />
              </div>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{docs.length} docs</span>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-2">
              {loading && <div className="p-6 text-center text-xs flex items-center justify-center gap-2" style={{ color: 'var(--text-muted)' }}><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>}
              {!loading && docs.length === 0 && <div className="p-6 text-center text-xs" style={{ color: 'var(--text-muted)' }}>No documents match.</div>}
              {!loading && docs.map((d) => {
                const id = d.doc_id || d.id;
                const isSel = selected.has(id);
                return (
                  <div key={id} onClick={() => toggle(id)}
                    className="flex items-center gap-2 p-2 rounded cursor-pointer hover:bg-black/5"
                    style={{ borderLeft: `3px solid ${isSel ? 'var(--accent-primary)' : 'transparent'}` }}
                    data-testid={`${testId}-picker-row-${id}`}>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(id)} />
                    <FileText className="w-3.5 h-3.5" style={{ color: 'var(--accent-primary)' }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[11px] font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                        <span style={{ color: 'var(--accent-primary)' }}>{d.doc_number || '—'}</span> · {d.title || 'untitled'}
                      </div>
                      <div className="text-[9px] flex gap-2" style={{ color: 'var(--text-muted)' }}>
                        {d.discipline && <span>{d.discipline}</span>}
                        {d.status && <span>· {d.status}</span>}
                        {d.folder && <span>· {d.folder}</span>}
                        {d.revision && <span>· Rev {d.revision}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="p-3 border-t flex justify-between items-center" style={{ borderColor: 'var(--border-color)' }}>
              <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{selected.size} selected</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={() => setPickerOpen(false)} data-testid={`${testId}-picker-cancel`}>Cancel</Button>
                <Button size="sm" onClick={confirmPick} disabled={selected.size === 0} data-testid={`${testId}-picker-confirm`}>
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Link {selected.size || ''}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
